import { degToRad, radToDeg } from '@rcs/domain';
import {
  canSetClosed,
  patchControlPoint,
  selectedControlPoint,
  type ControlPointPatch,
  type EditorState,
} from '@rcs/editor';
import { messages } from '../i18n/ja';
import { NumberField } from './NumberField';

/** 画面で選べる区間種別。 */
const SEGMENT_OPTIONS = ['normal', 'lift', 'launch', 'brake', 'station', 'block'] as const;

/**
 * プロパティパネル：選択中の制御点の位置（m）・バンク角（度）・区間種別とパラメータを数値で編集し、
 * コースの周回・開いたコースを切り替える（FR-EDT-002, 003, 004）。
 * バンク角は、度で入力し、ラジアンで保存する（SI 単位）。
 */
export function PropertiesPanel({
  state,
  onUpdate,
  onSetClosed,
}: {
  readonly state: EditorState;
  /** 変更を反映する。反映できなければ、理由（日本語）を返す */
  readonly onUpdate: (id: string, patch: ControlPointPatch) => string | undefined;
  readonly onSetClosed: (closed: boolean) => void;
}) {
  const t = messages.properties;
  const point = selectedControlPoint(state);
  const index = point ? state.course.controlPoints.findIndex((p) => p.id === point.id) : -1;

  return (
    <div className="properties" data-testid="properties">
      <label className="properties__closed">
        <input
          type="checkbox"
          checked={state.course.closed}
          disabled={!canSetClosed(state, !state.course.closed)}
          onChange={(event) => {
            onSetClosed(event.target.checked);
          }}
          data-testid="closed-toggle"
        />
        <span>{t.closed}</span>
      </label>
      {!canSetClosed(state, true) && !state.course.closed && (
        <p className="placeholder">{t.closedNeedsThree}</p>
      )}

      {point === undefined ? (
        <p className="placeholder" data-testid="properties-empty">
          {t.noSelection}
        </p>
      ) : (
        <section aria-label={t.title} key={point.id}>
          <h3 className="properties__title" data-testid="properties-title">
            {`${messages.controlPoints.point} ${String(index + 1)}`}
          </h3>
          {(['x', 'y', 'z'] as const).map((axis, i) => (
            <NumberField
              key={`${axis}:${String(point.position[i])}`}
              label={t.axes[axis]}
              unit="m"
              value={point.position[i] ?? 0}
              decimals={2}
              step={0.1}
              testId={`prop-${axis}`}
              onCommit={(value) => {
                const position: [number, number, number] = [...point.position];
                position[i] = value;
                return onUpdate(point.id, { position });
              }}
            />
          ))}
          <NumberField
            key={`bank:${String(point.bank)}`}
            label={t.bank}
            unit="°"
            value={radToDeg(point.bank)}
            decimals={1}
            step={1}
            testId="prop-bank"
            onCommit={(value) => onUpdate(point.id, { bank: degToRad(value) })}
          />
          <div className="number-field">
            <label htmlFor="prop-segment" className="number-field__label">
              {t.segment}
            </label>
            <select
              id="prop-segment"
              value={point.segment}
              data-testid="prop-segment"
              onChange={(event) => {
                onUpdate(point.id, {
                  segment: event.target.value as (typeof SEGMENT_OPTIONS)[number],
                });
              }}
            >
              {SEGMENT_OPTIONS.map((kind) => (
                <option key={kind} value={kind}>
                  {t.segments[kind]}
                </option>
              ))}
            </select>
          </div>
          <SegmentParams point={point} onUpdate={onUpdate} />
        </section>
      )}
    </div>
  );
}

/** 区間種別ごとのパラメータの入力欄。 */
function SegmentParams({
  point,
  onUpdate,
}: {
  readonly point: NonNullable<ReturnType<typeof selectedControlPoint>>;
  readonly onUpdate: (id: string, patch: ControlPointPatch) => string | undefined;
}) {
  const t = messages.properties.params;
  const field = (name: string, label: string, unit: string, value: number, key: string) => (
    <NumberField
      key={`${key}:${String(value)}`}
      label={label}
      unit={unit}
      value={value}
      decimals={1}
      step={0.5}
      testId={`prop-${name}`}
      onCommit={(next) => onUpdate(point.id, { segmentParams: { [name]: next } })}
    />
  );
  switch (point.segment) {
    case 'lift':
      return field('liftSpeed', t.liftSpeed, 'm/s', point.segmentParams.liftSpeed, 'lift');
    case 'brake':
      return (
        <>
          {field(
            'maxDeceleration',
            t.maxDeceleration,
            'm/s²',
            point.segmentParams.maxDeceleration,
            'brake-a',
          )}
          {field('targetSpeed', t.targetSpeed, 'm/s', point.segmentParams.targetSpeed, 'brake-v')}
        </>
      );
    case 'launch':
      return (
        <>
          {field(
            'targetSpeed',
            t.launchTargetSpeed,
            'm/s',
            point.segmentParams.targetSpeed,
            'launch-v',
          )}
          {field(
            'maxAcceleration',
            t.maxAcceleration,
            'm/s²',
            point.segmentParams.maxAcceleration,
            'launch-a',
          )}
        </>
      );
    case 'station':
      return field(
        'dispatchSpeed',
        t.dispatchSpeed,
        'm/s',
        point.segmentParams.dispatchSpeed,
        'station',
      );
    default:
      return null;
  }
}

/** 変更を適用した結果を検証して、反映できない理由を返す（反映できれば `undefined`）。 */
export function validatePatch(
  state: EditorState,
  id: string,
  patch: ControlPointPatch,
): string | undefined {
  const point = state.course.controlPoints.find((p) => p.id === id);
  if (!point) return messages.properties.notFound;
  const result = patchControlPoint(point, patch);
  return result.ok ? undefined : result.message;
}
